import type {
  DynamicTrackEventSnapshot,
  DynamicTrackEventPhase,
  MapDefinition,
  RaceEventMode,
  RacePhase,
  RaceSettings,
} from './types.js';
import { trackPoseAtIndex } from './track.js';

export const RACE_EVENT_MODES = new Set<RaceEventMode>([
  'circuit',
  'sprint',
  'time-trial',
  'style-rush',
  'elimination',
  'heat-run',
  'road-rage',
]);

export const DYNAMIC_EVENT_WARNING_MS = 5_000;
export const DYNAMIC_EVENT_ACTIVE_MS = 7_000;
export const DYNAMIC_EVENT_CLEARING_MS = 2_400;
export const DYNAMIC_EVENT_DEPLOYMENT_MS = 900;
export const DYNAMIC_EVENT_BARRIER_HEIGHT = 0.68;
export const DYNAMIC_EVENT_BARRIER_DEPTH = 0.5;
/** The rendered rail sits 8 cm above the road so it does not z-fight. */
export const DYNAMIC_EVENT_BARRIER_BASE_OFFSET = 0.08;

export interface DynamicTrackEventObstaclePose {
  eventId: number;
  trackIndex: number;
  side: 'left' | 'right';
  deployment: number;
  lateralOffset: number;
  /** Centre of the oriented obstacle volume. */
  x: number;
  y: number;
  z: number;
  /** Centre of the obstacle's lower face. */
  baseX: number;
  baseY: number;
  baseZ: number;
  yaw: number;
  width: number;
  length: number;
  height: number;
  forwardX: number;
  forwardY: number;
  forwardZ: number;
  rightX: number;
  rightY: number;
  rightZ: number;
  upX: number;
  upY: number;
  upZ: number;
}

export interface DynamicTrackEventObstacleMotion {
  eventId: number;
  current: DynamicTrackEventObstaclePose;
  previous: DynamicTrackEventObstaclePose;
  velocityX: number;
  velocityY: number;
  velocityZ: number;
}

/** Authoritative 0..1 deployment shared by collision and rendering. The
 * barrier stays off the racing surface for most of the warning, moves in
 * during its final 900 ms, and physically retracts throughout clearing. */
export function dynamicTrackEventDeployment(
  phase: DynamicTrackEventPhase,
  remainingMs: number,
): number {
  if (phase === 'active') return 1;
  if (phase === 'clearing') {
    return clampUnit(remainingMs / DYNAMIC_EVENT_CLEARING_MS);
  }
  return clampUnit((DYNAMIC_EVENT_DEPLOYMENT_MS - remainingMs) / DYNAMIC_EVENT_DEPLOYMENT_MS);
}

export function dynamicTrackEventBarrierHalfWidth(roadWidth: number): number {
  return Math.max(1.7, roadWidth * 0.14);
}

/** Signed lateral centre. At zero deployment the complete obstacle is parked
 * beyond the road edge; at one it closes only the authored lane. */
export function dynamicTrackEventLateralOffset(
  roadWidth: number,
  side: 'left' | 'right',
  deployment: number,
): number {
  const sign = side === 'right' ? 1 : -1;
  const deployed = roadWidth * 0.24;
  const parked = roadWidth * 0.5 + dynamicTrackEventBarrierHalfWidth(roadWidth) + 0.8;
  return sign * (parked + (deployed - parked) * clampUnit(deployment));
}

/** One authoritative oriented obstacle pose for server collision, AI planning,
 * traffic and client rendering. Offsets and height follow the banked road
 * frame instead of assuming a flat world-up surface. */
export function dynamicTrackEventObstaclePose(
  map: MapDefinition,
  event: Pick<
    DynamicTrackEventSnapshot,
    'eventId' | 'trackIndex' | 'side' | 'deployment'
  >,
): DynamicTrackEventObstaclePose {
  const road = trackPoseAtIndex(map, event.trackIndex);
  const deployment = clampUnit(event.deployment);
  const lateralOffset = dynamicTrackEventLateralOffset(
    road.width,
    event.side,
    deployment,
  );
  const surfaceX = road.x + road.rightX * lateralOffset;
  const surfaceY = road.y + road.rightY * lateralOffset;
  const surfaceZ = road.z + road.rightZ * lateralOffset;
  const baseX = surfaceX + road.normalX * DYNAMIC_EVENT_BARRIER_BASE_OFFSET;
  const baseY = surfaceY + road.normalY * DYNAMIC_EVENT_BARRIER_BASE_OFFSET;
  const baseZ = surfaceZ + road.normalZ * DYNAMIC_EVENT_BARRIER_BASE_OFFSET;
  const halfHeight = DYNAMIC_EVENT_BARRIER_HEIGHT * 0.5;
  return {
    eventId: event.eventId,
    trackIndex: event.trackIndex,
    side: event.side,
    deployment,
    lateralOffset,
    x: baseX + road.normalX * halfHeight,
    y: baseY + road.normalY * halfHeight,
    z: baseZ + road.normalZ * halfHeight,
    baseX,
    baseY,
    baseZ,
    yaw: Math.atan2(road.tangentX, road.tangentZ),
    width: dynamicTrackEventBarrierHalfWidth(road.width) * 1.85,
    length: DYNAMIC_EVENT_BARRIER_DEPTH,
    height: DYNAMIC_EVENT_BARRIER_HEIGHT,
    forwardX: road.tangentX,
    forwardY: road.tangentY,
    forwardZ: road.tangentZ,
    rightX: road.rightX,
    rightY: road.rightY,
    rightZ: road.rightZ,
    upX: road.normalX,
    upY: road.normalY,
    upZ: road.normalZ,
  };
}

/** Previous/current pose and finite-difference obstacle velocity. A missing or
 * different previous event is treated as a spawn, never as a world-spanning
 * sweep from the old obstacle. */
export function dynamicTrackEventObstacleMotion(
  map: MapDefinition,
  event: DynamicTrackEventSnapshot,
  previousEvent: DynamicTrackEventSnapshot | null,
  dtSecondsValue: number,
): DynamicTrackEventObstacleMotion {
  const current = dynamicTrackEventObstaclePose(map, event);
  const previous = previousEvent?.eventId === event.eventId
    ? dynamicTrackEventObstaclePose(map, previousEvent)
    : current;
  const dtSeconds = Math.max(
    1 / 1_000,
    Math.min(0.1, Number.isFinite(dtSecondsValue) ? dtSecondsValue : 0),
  );
  return {
    eventId: event.eventId,
    current,
    previous,
    velocityX: (current.x - previous.x) / dtSeconds,
    velocityY: (current.y - previous.y) / dtSeconds,
    velocityZ: (current.z - previous.z) / dtSeconds,
  };
}

/** Approximate the banked lower face directly below an x/z contact. This keeps
 * bridge, jump and bank separation consistent without needing a full 3D SAT
 * solver for a low obstacle. */
export function dynamicTrackEventBaseYAt(
  pose: DynamicTrackEventObstaclePose,
  x: number,
  z: number,
): number {
  const dx = x - pose.baseX;
  const dz = z - pose.baseZ;
  const determinant = pose.rightX * pose.forwardZ - pose.rightZ * pose.forwardX;
  const safeDeterminant = Math.abs(determinant) >= 1e-6
    ? determinant
    : determinant < 0 ? -1e-6 : 1e-6;
  const localRight = clamp(
    (dx * pose.forwardZ - dz * pose.forwardX) / safeDeterminant,
    -pose.width * 0.5,
    pose.width * 0.5,
  );
  const localForward = clamp(
    (pose.rightX * dz - pose.rightZ * dx) / safeDeterminant,
    -pose.length * 0.5,
    pose.length * 0.5,
  );
  return pose.baseY + pose.rightY * localRight + pose.forwardY * localForward;
}

/** Resolves old boolean-only room saves without changing their behaviour. */
export function raceEventMode(settings: Pick<RaceSettings, 'eventMode' | 'timeTrial'>): RaceEventMode {
  if (settings.eventMode && RACE_EVENT_MODES.has(settings.eventMode)) return settings.eventMode;
  return settings.timeTrial === true ? 'time-trial' : 'circuit';
}

export function isTimeTrialMode(settings: Pick<RaceSettings, 'eventMode' | 'timeTrial'>): boolean {
  return raceEventMode(settings) === 'time-trial';
}

export function eventModeLabel(mode: RaceEventMode): string {
  switch (mode) {
    case 'sprint': return 'STREET SPRINT';
    case 'time-trial': return 'TIME TRIAL';
    case 'style-rush': return 'STYLE RUSH';
    case 'elimination': return 'ELIMINATION';
    case 'heat-run': return 'HEAT RUN';
    case 'road-rage': return 'ROAD RAGE';
    default: return 'CIRCUIT RACE';
  }
}

/** Sprint events deliberately reuse the complete authored circuit but resolve
 * after one lap. Keeping this rule shared prevents the HUD, audio and server
 * authority from disagreeing about the chequered flag. */
export function raceLapCount(
  settings: Pick<RaceSettings, 'eventMode' | 'timeTrial'> & Partial<Pick<RaceSettings, 'laps'>>,
  authoredLaps: number,
): number {
  const mode = raceEventMode(settings);
  if (mode === 'sprint') return 1;
  if (
    settings.laps !== undefined
    && Number.isInteger(settings.laps)
    && (mode === 'circuit' || mode === 'elimination' || mode === 'time-trial' || mode === 'style-rush')
  ) return Math.max(1, Math.min(10, settings.laps));
  // Heat Run is an escalation ladder, not a shortened circuit. Five levels
  // guarantee that both H1 and H3 interceptor deployments have meaningful
  // play time even on authored two- or three-lap tracks. Drivers may bank
  // after any completed lap; crossing the H5 cap banks automatically.
  if (mode === 'heat-run') return Math.max(5, Math.floor(authoredLaps));
  if (mode === 'road-rage') return Math.max(2, Math.floor(authoredLaps));
  return Math.max(1, Math.floor(authoredLaps));
}

/** One source of truth for the client shortcut and authoritative server gate. */
/** Solo drivers may restart from the results or straight from the pause
 * menu mid-race; restarting during the countdown would only stutter the grid. */
export function canQuickRestart(_mode: RaceEventMode, phase: RacePhase): boolean {
  return phase === 'finished' || phase === 'racing';
}

/** Player-facing classification text for modes where finishedAt has a
 * mode-specific meaning (for example, elimination timestamps a knockout). */
export function eventResultLabel(
  mode: RaceEventMode,
  place: number,
  finishedAt: number | null,
): 'WIN' | 'OUT' | 'SCORED' | 'FINISHED' | 'DNF' | 'BUST' {
  if (mode === 'elimination') return place === 1 ? 'WIN' : 'OUT';
  if (mode === 'style-rush') {
    if (finishedAt === null) return 'DNF';
    return place === 1 ? 'WIN' : 'SCORED';
  }
  if (mode === 'heat-run') {
    if (finishedAt === null) return 'BUST';
    return place === 1 ? 'WIN' : 'SCORED';
  }
  if (mode === 'road-rage') {
    if (finishedAt === null) return 'DNF';
    return place === 1 ? 'WIN' : 'SCORED';
  }
  return finishedAt === null ? 'DNF' : 'FINISHED';
}

function clampUnit(value: number): number {
  return Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0));
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}
